import { useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";
import { z } from "zod";
import { AlertCircle, X } from "lucide-react";
import { epicengageSupabasev1 } from "../../../../../integrations/supabase/client";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../../../ui/dialog";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Button } from "../../../../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../../ui/select";
import { Alert, AlertDescription, AlertTitle } from "../../../../ui/alert";
import { useToast } from "../../../../../hooks/use-toast";
import { STATUS_OPTIONS, STATUS_LABEL, CustomerStatus } from "../../../../../lib/customersEngageV1";
import { swatchForTag, fallbackTagColorKey } from "../../../../../lib/tagColors";

const schema = z.object({
  name: z.string().trim().max(120).optional(),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z.string().trim().max(40).optional(),
  business_name: z.string().trim().max(120).optional(),
  status: z.enum(["new", "active", "inactive", "unsubscribed"]),
});

type MerchantTag = { tag: string; color: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  /** Kept for backwards compatibility; no longer used — suggestions come from merchant_tags. */
  tagSuggestions?: string[];
  onSaved: () => void;
};

type ExistingMatch = { id: string; name: string | null; email: string };

export const AddCustomerDialog = ({ open, onOpenChange, merchantId, onSaved }: Props) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [status, setStatus] = useState<CustomerStatus>("new");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  const [merchantTags, setMerchantTags] = useState<MerchantTag[]>([]);
  const [duplicate, setDuplicate] = useState<ExistingMatch | null>(null);
  const tagBoxRef = useRef<HTMLDivElement>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setBusinessName("");
    setStatus("new"); setTags([]); setTagInput(""); setDuplicate(null);
  };

  // Load merchant_tags whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data } = await epicengageSupabasev1
        .from("merchant_tags")
        .select("tag,color")
        .eq("merchant_id", merchantId)
        .order("tag", { ascending: true });
      if (!cancelled) setMerchantTags((data ?? []) as MerchantTag[]);
    })();
    return () => { cancelled = true; };
  }, [open, merchantId]);

  const colorFor = (tag: string) =>
    merchantTags.find((t) => t.tag === tag)?.color ?? fallbackTagColorKey(tag);

  const filteredSuggestions = useMemo(() => {
    const q = tagInput.trim().toLowerCase();
    return merchantTags
      .filter((t) => !tags.includes(t.tag) && (!q || t.tag.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [merchantTags, tags, tagInput]);

  const exactExists = useMemo(
    () => merchantTags.some((t) => t.tag === tagInput.trim().toLowerCase()),
    [merchantTags, tagInput]
  );

  const addTag = (raw: string) => {
    const t = raw.trim().toLowerCase();
    if (!t || tags.includes(t)) { setTagInput(""); return; }
    setTags((prev) => [...prev, t]);
    setTagInput("");
  };
  const removeTag = (t: string) => setTags((prev) => prev.filter((x) => x !== t));

  const onTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      e.stopPropagation();
      if (tagInput.trim()) addTag(tagInput);
    } else if (e.key === "Backspace" && !tagInput && tags.length) {
      removeTag(tags[tags.length - 1]);
    }
  };

  /** Persist any tag names that don't yet exist in merchant_tags. */
  const ensureMerchantTags = async (tagList: string[]) => {
    const existing = new Set(merchantTags.map((t) => t.tag));
    const missing = tagList.filter((t) => !existing.has(t));
    if (!missing.length) return;
    const rows = missing.map((tag) => ({
      merchant_id: merchantId,
      tag,
      color: fallbackTagColorKey(tag),
    }));
    await epicengageSupabasev1
      .from("merchant_tags")
      .upsert(rows, { onConflict: "merchant_id,tag", ignoreDuplicates: true });
  };

  const writeCustomerTags = async (customerId: string, tagList: string[]) => {
    if (!tagList.length) return;
    const rows = tagList.map((tag) => ({
      merchant_id: merchantId, customer_id: customerId, tag,
    }));
    const { error } = await epicengageSupabasev1
      .from("customer_tags")
      .upsert(rows, { onConflict: "customer_id,tag", ignoreDuplicates: true });
    if (error) throw error;
  };

  /** Insert new customer (no upsert). Returns inserted row. */
  const insertNew = async (parsed: z.infer<typeof schema>) => {
    const { data, error } = await epicengageSupabasev1
      .from("customers")
      .insert({
        merchant_id: merchantId,
        name: parsed.name ?? null,
        email: parsed.email.toLowerCase(),
        phone: parsed.phone ?? null,
        business_name: parsed.business_name ?? null,
        status: parsed.status,
        source: "manual",
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  };

  /** Update an existing customer by id. */
  const updateExisting = async (id: string, parsed: z.infer<typeof schema>, restore = false) => {
    const patch: any = {
      name: parsed.name ?? null,
      phone: parsed.phone ?? null,
      business_name: parsed.business_name ?? null,
      status: parsed.status,
    };
    if (restore) {
      patch.archived = false;
      patch.archived_at = null;
    }
    const { data, error } = await epicengageSupabasev1
      .from("customers")
      .update(patch)
      .eq("id", id)
      .eq("merchant_id", merchantId)
      .select()
      .single();
    if (error) throw error;
    return data;
  };

  const validate = () => {
    const parsed = schema.safeParse({
      name: name || undefined,
      email,
      phone: phone || undefined,
      business_name: businessName || undefined,
      status,
    });
    if (!parsed.success) {
      toast({ title: "Invalid input", description: parsed.error.errors[0].message, variant: "destructive" });
      return null;
    }
    return parsed.data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = validate();
    if (!parsed) return;

    setSubmitting(true);
    try {
      // Pre-check duplicate
      const { data: existing, error: lookupErr } = await epicengageSupabasev1
        .from("customers")
        .select("id,name,email,archived")
        .eq("merchant_id", merchantId)
        .eq("email", parsed.email.toLowerCase())
        .maybeSingle();
      if (lookupErr) throw lookupErr;

      if (existing) {
        // Show inline warning, do NOT save yet
        setDuplicate({ ...(existing as any), archived: !!(existing as any).archived } as ExistingMatch);
        setSubmitting(false);
        return;
      }

      const inserted = await insertNew(parsed);
      if (tags.length) {
        await ensureMerchantTags(tags);
        await writeCustomerTags(inserted.id, tags);
      }
      toast({ title: "Customer added successfully" });
      reset();
      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      toast({
        title: "Could not save customer",
        description: err.message ?? "Try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmUpdate = async () => {
    const parsed = validate();
    if (!parsed || !duplicate) return;
    setSubmitting(true);
    try {
      await updateExisting(duplicate.id, parsed, duplicate.archived);
      if (tags.length) {
        await ensureMerchantTags(tags);
        await writeCustomerTags(duplicate.id, tags);
      }
      toast({ title: "Customer updated successfully" });
      reset();
      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      toast({
        title: "Could not update customer",
        description: err.message ?? "Try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add customer</DialogTitle>
          <DialogDescription>We'll check for an existing customer with the same email before saving.</DialogDescription>
        </DialogHeader>

        {duplicate && (
          <Alert className="border-destructive/40 bg-destructive/5 text-foreground [&>svg]:text-destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{duplicate.archived ? "Customer exists (archived)" : "Customer already exists"}</AlertTitle>
            <AlertDescription>
              A customer with <span className="font-medium">{duplicate.email}</span> already exists
              {duplicate.name ? <> ({duplicate.name})</> : null}
              {duplicate.archived
                ? <> but is currently <span className="font-medium">archived</span>. Restore and update their details?</>
                : <>. Do you want to update their details?</>}
              <div className="mt-3 flex gap-2">
                <Button type="button" size="sm" onClick={handleConfirmUpdate} disabled={submitting}>
                  {submitting ? (duplicate.archived ? "Restoring…" : "Updating…") : (duplicate.archived ? "Restore & update" : "Update existing")}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => { reset(); onOpenChange(false); }}>
                  Cancel
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (duplicate) setDuplicate(null); }}
              required
              maxLength={255}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bn">Business name</Label>
              <Input id="bn" value={businessName} onChange={(e) => setBusinessName(e.target.value)} maxLength={120} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as CustomerStatus)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Tags</Label>
            <div ref={tagBoxRef} className="relative">
              <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-input bg-background min-h-10">
                {tags.map((t) => {
                  const sw = swatchForTag(colorFor(t));
                  return (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                      style={{ background: sw.bg, color: sw.fg }}
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => removeTag(t)}
                        className="hover:opacity-70"
                        aria-label={`Remove ${t}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
                <Input
                  value={tagInput}
                  onChange={(e) => { setTagInput(e.target.value); setSuggestOpen(true); }}
                  onKeyDown={onTagKeyDown}
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => {
                    // Delay to allow suggestion clicks
                    setTimeout(() => setSuggestOpen(false), 120);
                    if (tagInput.trim()) addTag(tagInput);
                  }}
                  placeholder={tags.length ? "Add another tag…" : "Type to search or create a tag"}
                  className="border-0 shadow-none h-6 px-1 flex-1 min-w-32 focus-visible:ring-0"
                />
              </div>

              {suggestOpen && (filteredSuggestions.length > 0 || (tagInput.trim() && !exactExists)) && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md p-1 max-h-56 overflow-auto">
                  {filteredSuggestions.map((t) => {
                    const sw = swatchForTag(t.color);
                    return (
                      <button
                        key={t.tag}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); addTag(t.tag); }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent text-left text-sm"
                      >
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: sw.dot }} />
                        <span>{t.tag}</span>
                      </button>
                    );
                  })}
                  {tagInput.trim() && !exactExists && (
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); addTag(tagInput); }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent text-left text-sm border-t border-border mt-1"
                    >
                      <Plus />
                      Create &ldquo;{tagInput.trim().toLowerCase()}&rdquo;
                    </button>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Pick from existing tags or type a new one — new tags are saved to your tag library.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting || !!duplicate}>
              {submitting ? "Saving…" : "Save customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Local Plus icon to avoid extra import churn
function Plus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
